import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { EndpointBuilder } from '@reduxjs/toolkit/query/endpointDefinitions';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/fetchBaseQuery';
import { 
  Photo,
  PhotoListResponse,
  PhotoUploadResponse 
} from '../../types/photos';
import type { ApiResponse } from '../../types/api';
import type { ZipRequestResponse, ZipStatusResponse } from '../../types/event';

// Photo API endpoints
export const photoApiEndpoints = (builder: EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, 'api'>) => ({
  getEventPhotos: builder.query<Photo[], string>({
    query: (url) => `/photos/event/${url}`,
    transformResponse: (response: PhotoListResponse) => response.data,
    providesTags: (_result, _error, url) => [{ type: 'EventPhotos', id: url }],
  }),
  
  getPublicEventPhotos: builder.query<Photo[], string>({
    query: (url) => ({
      url: `/gallery/${url}`,
      credentials: 'omit'
    }),
    transformResponse: (response: ApiResponse<Photo[]>) => response.data,
    providesTags: [{ type: 'Photos', id: 'PUBLIC' }]
  }),
  
  requestPhotosZip: builder.mutation<ZipRequestResponse['data'], number>({
    query: (eventId) => ({
      url: '/photos/download-zip',
      method: 'POST',
      body: { event_id: eventId },
    }),
    transformResponse: (response: ZipRequestResponse) => response.data,
  }),
  
  checkZipStatus: builder.query<ZipStatusResponse['data'], string>({
    query: (jobId) => `/photos/download-status/${jobId}`,
    transformResponse: (response: ZipStatusResponse) => response.data,
  }),
  
  uploadGuestPhoto: builder.mutation<
    PhotoUploadResponse['data'],
    { url: string; file: File; onProgress?: (progress: number) => void }
  >({
    async queryFn({ url, file }, _api, _extraOptions, fetchWithBQ) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        // API URL structure
        const apiUrl = import.meta.env.VITE_API_URL || 'https://ourphotos-backend.fly.dev/api';
        const endpoint = `/events/guest-upload/${url}`;
        const fullUrl = apiUrl + endpoint;
        
        // Simple fetch implementation
        const response = await fetch(fullUrl, {
          method: 'POST',
          body: formData,
          credentials: 'omit'
        });
        
        let responseData;
        try {
          responseData = await response.json();
        } catch (e) {
          responseData = { message: 'Failed to parse response' };
        }
        
        if (!response.ok) {
          return { 
            error: { 
              status: response.status, 
              data: responseData || { message: response.statusText } 
            } 
          };
        }
        
        return { data: responseData };
      } catch (error: any) {
        return {
          error: {
            status: 500,
            data: { message: error.message || 'An unknown error occurred' }
          }
        };
      }
    },
    invalidatesTags: [{ type: 'Photos', id: 'PUBLIC' }]
  }),
  
  uploadEventPhoto: builder.mutation<PhotoUploadResponse['data'], { eventUrl: string; file: File }>({
    query: ({ eventUrl, file }) => {
      const formData = new FormData();
      formData.append('files', file);
      
      return {
        url: `/events/${eventUrl}/photos`,
        method: 'POST',
        body: formData,
      };
    },
    invalidatesTags: (_result, _error, { eventUrl }) => [{ type: 'EventPhotos', id: eventUrl }],
  }),
}); 